import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, ExternalLink, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { WORLDS } from "@/lib/worlds";
import { gbp, prettyDate } from "@/lib/format";
import { usePageMeta } from "@/lib/site";
import { providerApi, billingApi, BOOKING_STATUS } from "@/lib/providerApi";
import { Stat, Empty } from "@/components/partner/PartnerShell";
import { PlanPicker } from "@/components/partner/PlanPicker";
import { ListingForm, emptyListing, listingToForm, formToBody, validateListing, useModules } from "@/components/partner/ListingForm";

const worldOf = (key) => WORLDS.find((w) => w.key === key);

function useMyListings() {
  const [items, setItems] = useState(undefined);
  const load = () => providerApi.myListings().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return [items, load];
}

// ---------------------------------------------------------------------------------------- overview
export function PartnerOverview() {
  usePageMeta("Partner hub", "Your bookings, listings and earnings on Fable +.");
  const { user } = useAuth();
  const [listings] = useMyListings();
  const mods = useMemo(() => [...new Set((listings || []).map((l) => l.module))], [listings]);
  const [module, setModule] = useState(null);
  const [dash, setDash] = useState(undefined);
  const active = module || mods[0] || "services";

  useEffect(() => {
    if (listings === undefined) return;
    setDash(undefined);
    providerApi.dashboard(active).then(setDash).catch(() => setDash(null));
  }, [active, listings]);

  if (user && !user.is_partner && listings && listings.length === 0) return <NotYetPartner />;

  return (
    <div>
      {mods.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {mods.map((m) => (
            <button key={m} type="button" onClick={() => setModule(m)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${m === active ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{worldOf(m)?.name || m}</button>
          ))}
        </div>
      )}
      {dash === undefined ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : dash ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Booked value" value={gbp(dash.stats.revenue)} sub={`${dash.stats.bookings} bookings all time`} />
            <Stat label="This week" value={gbp(dash.week_compare?.this_week?.takings || 0)} sub={dash.week_compare ? `${dash.week_compare.delta_pct >= 0 ? "+" : ""}${dash.week_compare.delta_pct}% vs last week to date` : ""} />
            <Stat label="Upcoming" value={dash.stats.upcoming} sub={`${dash.stats.completed_today_count} completed today`} />
            <Stat label="Rating" value={dash.stats.rating ? dash.stats.rating.toFixed(1) : "—"} sub={`${dash.stats.listings} listing${dash.stats.listings === 1 ? "" : "s"} in ${worldOf(active)?.name || active}`} />
          </div>
          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
            <section className="rounded-2xl border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="font-bold">Upcoming bookings</h2>
                <Link to="/partner/bookings" className="text-sm font-semibold text-primary hover:underline">All bookings →</Link>
              </div>
              {dash.upcoming?.length ? (
                <ul className="divide-y divide-border">
                  {dash.upcoming.slice(0, 8).map((b) => (
                    <li key={b.id} className="flex items-center gap-4 px-5 py-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-semibold">{b.service_name || b.listing_title}</p>
                        <p className="text-xs text-muted-foreground">{b.date ? prettyDate(b.date) : "Date TBC"}{b.time_slot ? ` · ${b.time_slot}` : ""}</p>
                      </div>
                      <StatusPill status={b.status} />
                      <span className="w-16 text-right font-semibold">{gbp(b.total_price)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nothing booked in yet. Share your listing to get the first one.</p>
              )}
            </section>
            <aside className="space-y-4">
              <QuickLinks />
              <WeekStrip series={dash.series} />
            </aside>
          </div>
        </>
      ) : (
        <Empty title="Couldn't load your dashboard" body="Please refresh in a moment." />
      )}
    </div>
  );
}

function NotYetPartner() {
  return (
    <div className="rounded-3xl border border-border bg-card p-8 sm:p-12">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--provider)" }}>Become a partner</p>
      <h2 className="mt-2 font-serif-display text-3xl font-bold">Your whole business, in one dashboard</h2>
      <p className="mt-3 max-w-xl text-muted-foreground">List in any of the six worlds with one plan, take bookings and card payments, and manage everything here or in the app. Subscription plans are 0% commission; pay as you go is 2.7% with no monthly fee.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/partner/start"><Button className="rounded-full px-6" data-testid="partner-start-cta">Start — it takes 5 minutes</Button></Link>
        <Link to="/pricing"><Button variant="outline" className="rounded-full px-6">See pricing</Button></Link>
      </div>
    </div>
  );
}

function QuickLinks() {
  const links = [
    ["/partner/listings/new", "Add a listing"],
    ["/partner/bookings", "Manage bookings"],
    ["/partner/earnings", "Earnings & payouts"],
    ["/partner/plan", "Plan & billing"],
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <h3 className="font-bold">Quick links</h3>
      <ul className="mt-3 space-y-2 text-sm">
        {links.map(([to, label]) => <li key={to}><Link to={to} className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-muted">{label} <ArrowRight size={14} /></Link></li>)}
      </ul>
      <p className="mt-4 text-xs text-muted-foreground">Team seats, shifts, marketing, Stage and more are in the Fable + app.</p>
    </div>
  );
}

function WeekStrip({ series = [] }) {
  const max = Math.max(1, ...series.map((d) => d.value));
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <h3 className="font-bold">Last 7 days</h3>
      <div className="mt-4 flex h-24 items-end gap-2">
        {series.map((d) => (
          <div key={d.date} className="flex flex-1 flex-col items-center gap-1" title={`${d.label}: ${gbp(d.value)} · ${d.count} bookings`}>
            <div className="w-full rounded-t-md" style={{ height: `${Math.max(4, (d.value / max) * 80)}px`, background: "var(--fable)", opacity: d.value ? 1 : 0.2 }} />
            <span className="text-[10px] text-muted-foreground">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const s = BOOKING_STATUS[status] || { label: status || "—", tone: "bg-muted text-foreground/70" };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.tone}`}>{s.label}</span>;
}

// ---------------------------------------------------------------------------------------- listings
export function PartnerListings() {
  usePageMeta("Your listings", "Manage your Fable + listings.");
  const [items] = useMyListings();
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-xl font-bold">Listings</h2>
        <Link to="/partner/listings/new"><Button className="rounded-full" data-testid="listing-add"><Plus size={16} className="mr-1" /> Add a listing</Button></Link>
      </div>
      {items === undefined ? (
        <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <Empty title="No listings yet" body="Create your first listing — one plan covers every world." action={<Link to="/partner/start"><Button className="rounded-full">Get started</Button></Link>} />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {items.map((l) => {
            const w = worldOf(l.module);
            return (
              <li key={l.id} className="flex gap-4 rounded-2xl border border-border bg-card p-4" data-testid={`listing-row-${l.id}`}>
                <img src={l.image_url} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold" style={{ color: w?.accent }}>{w?.name || l.module} · {l.category}</p>
                  <h3 className="truncate font-bold">{l.title}</h3>
                  <p className="truncate text-xs text-muted-foreground">{l.subtitle || l.location}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-semibold">{gbp(l.price_min ?? l.price)}{l.price_max > (l.price_min ?? l.price) ? `–${gbp(l.price_max)}` : ""}</span>
                    {l.rating ? <span>★ {Number(l.rating).toFixed(1)} ({l.review_count || 0})</span> : <span className="text-muted-foreground">No reviews yet</span>}
                    {l.status && l.status !== "live" && l.status !== "active" ? <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">{l.status}</span> : null}
                  </div>
                  <div className="mt-2 flex gap-3 text-sm font-semibold">
                    <Link to={`/partner/listings/${l.id}`} className="text-primary hover:underline">Edit</Link>
                    <Link to={`/listing/${l.id}`} className="inline-flex items-center gap-1 text-muted-foreground hover:underline">View <ExternalLink size={12} /></Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function PartnerListingEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  usePageMeta(isNew ? "Add a listing" : "Edit listing");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mods = useModules();
  const [form, setForm] = useState(isNew ? emptyListing(params.get("world") || "") : null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    providerApi.myListings().then((all) => {
      const l = all.find((x) => x.id === id);
      if (!l) { toast.error("That listing isn't yours or no longer exists"); navigate("/partner/listings"); return; }
      setForm(listingToForm(l));
    }).catch(() => navigate("/partner/listings"));
  }, [id, isNew, navigate]);

  const save = async () => {
    const problem = validateListing(form);
    if (problem) return toast.error(problem);
    setBusy(true);
    try {
      if (isNew) {
        const created = await providerApi.createListing(formToBody(form));
        toast.success("Listing published");
        navigate(`/partner/listings/${created.id || ""}`.replace(/\/$/, ""), { replace: true });
      } else {
        await providerApi.patchListing(id, formToBody(form, { create: false }));
        toast.success("Saved");
      }
    } catch (e) {
      if (e.status === 402) {
        toast.error("Choose a plan first — then your listing goes live.");
        navigate("/partner/plan");
      } else toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <Skeleton className="h-96 rounded-2xl" />;
  return (
    <div className="max-w-3xl">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-xl font-bold">{isNew ? "Add a listing" : "Edit listing"}</h2>
        <Link to="/partner/listings" className="text-sm text-muted-foreground hover:underline">Back to listings</Link>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <ListingForm value={form} onChange={setForm} mode={isNew ? "create" : "edit"} mods={mods} />
        <Button className="mt-6 rounded-full" onClick={save} disabled={busy} data-testid="listing-save">{busy ? "Saving…" : isNew ? "Publish listing" : "Save changes"}</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- bookings
const FILTERS = [["upcoming", "Upcoming"], ["requests", "Requests"], ["past", "Past"], ["all", "All"]];

export function PartnerBookings() {
  usePageMeta("Bookings", "Manage your Fable + bookings.");
  const [cal, setCal] = useState(undefined);
  const [filter, setFilter] = useState("upcoming");
  const [busy, setBusy] = useState(null);

  const load = () => providerApi.calendar().then(setCal).catch(() => setCal(null));
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const items = useMemo(() => {
    const all = (cal?.items || cal?.bookings || []).slice().sort((a, b) => `${a.date || "9999"}${a.time_slot || ""}`.localeCompare(`${b.date || "9999"}${b.time_slot || ""}`));
    if (filter === "requests") return all.filter((b) => ["requested", "pending"].includes(b.status));
    if (filter === "upcoming") return all.filter((b) => (b.date || "9999") >= today && !["cancelled", "declined", "completed", "refunded", "no_show"].includes(b.status));
    if (filter === "past") return all.filter((b) => (b.date || "0") < today || ["completed", "cancelled", "declined", "refunded", "no_show"].includes(b.status)).reverse();
    return all;
  }, [cal, filter, today]);

  const act = async (b, status) => {
    setBusy(b.id + status);
    try {
      await providerApi.patchBooking(b.id, { status });
      toast.success(status === "confirmed" || status === "approved" ? "Accepted" : status === "completed" ? "Marked complete" : status === "declined" ? "Declined" : "Updated");
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">Bookings</h2>
        <div className="inline-flex rounded-full bg-muted p-1">
          {FILTERS.map(([k, label]) => <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`bookings-filter-${k}`} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${filter === k ? "bg-background shadow" : "text-muted-foreground"}`}>{label}</button>)}
        </div>
      </div>
      {cal === undefined ? <Skeleton className="h-64 rounded-2xl" /> : items.length === 0 ? (
        <Empty title={filter === "requests" ? "No requests waiting" : "No bookings here"} body={filter === "upcoming" ? "New bookings show up here the moment a customer books." : undefined} />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
          {items.map((b) => {
            const w = worldOf(b.module);
            const canAccept = ["requested", "pending"].includes(b.status);
            const canComplete = ["confirmed", "approved", "active"].includes(b.status) && (b.date || "") <= today;
            const canCancel = ["requested", "pending", "confirmed", "approved"].includes(b.status);
            return (
              <li key={b.id} className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm" data-testid={`booking-row-${b.id}`}>
                <div className="min-w-[200px] flex-1">
                  <p className="font-semibold">{b.service_name || b.listing_title}</p>
                  <p className="text-xs text-muted-foreground">
                    <span style={{ color: w?.accent }}>{w?.name || b.module}</span> · {b.customer_name || "Customer"} · {b.date ? prettyDate(b.date) : "Date TBC"}{b.time_slot ? ` · ${b.time_slot}` : ""}{b.end_date && b.end_date !== b.date ? ` → ${prettyDate(b.end_date)}` : ""}
                  </p>
                </div>
                <StatusPill status={b.status} />
                <span className="w-16 text-right font-semibold">{gbp(b.total_price)}</span>
                <div className="flex gap-2">
                  {canAccept && <Button size="sm" className="rounded-full" disabled={!!busy} onClick={() => act(b, b.module === "rentals" ? "approved" : "confirmed")} data-testid={`booking-accept-${b.id}`}>Accept</Button>}
                  {canAccept && <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => act(b, "declined")}>Decline</Button>}
                  {canComplete && <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => act(b, "completed")} data-testid={`booking-complete-${b.id}`}>Mark complete</Button>}
                  {!canAccept && canCancel && <Button size="sm" variant="ghost" className="rounded-full text-destructive" disabled={!!busy} onClick={() => { if (window.confirm("Cancel this booking? The customer is refunded under the policy shown on their booking.")) act(b, "cancelled"); }}>Cancel</Button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-4 text-xs text-muted-foreground">Rescheduling, staff assignment and messaging the customer are in the Fable + app for now.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- earnings
export function PartnerEarnings() {
  usePageMeta("Earnings & payouts");
  const [data, setData] = useState(undefined);
  const [connect, setConnect] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    providerApi.earnings(6).then(setData).catch(() => setData(null));
    providerApi.connectStatus().then(setConnect).catch(() => setConnect(null));
  }, []);

  const setupPayouts = async () => {
    setBusy(true);
    try {
      const r = await providerApi.connectOnboard(window.location.origin);
      window.location.assign(r.url);
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const t = data?.totals;
  return (
    <div>
      <h2 className="mb-5 text-xl font-bold">Earnings & payouts</h2>
      {data === undefined ? <Skeleton className="h-28 rounded-2xl" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Gross (6 months)" value={gbp(t?.gross || 0)} sub={`${t?.count || 0} payments`} />
          <Stat label="Fable + fees" value={gbp(t?.fable_fee || 0)} sub="0% on subscription plans; 2.7% on pay as you go; 5% on rentals" />
          <Stat label="Card processing" value={gbp(t?.stripe_fee || 0)} sub={t?.estimated_fees ? "includes estimates" : "Stripe fees"} />
          <Stat label="Net to you" value={gbp(t?.net || 0)} />
        </div>
      )}

      <section className="mt-8 rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Wallet className="text-primary" />
          <div className="flex-1">
            <h3 className="font-bold">Payouts</h3>
            <p className="text-sm text-muted-foreground">
              {connect === undefined ? "Checking your payout account…"
                : connect && connect.onboarding_available === false ? "Payout setup opens when card payments go live on Fable +. Cash bookings work now."
                : connect?.payouts_enabled ? "Card payments go straight to your bank through Stripe."
                : connect?.account_id ? "Your payout account needs a few more details before Stripe can pay you."
                : "Set up payouts once and card payments land in your bank automatically. Cash bookings don't need this."}
            </p>
          </div>
          {connect !== undefined && connect?.onboarding_available !== false && !connect?.payouts_enabled && <Button className="rounded-full" onClick={setupPayouts} disabled={busy} data-testid="connect-setup">{connect?.account_id ? "Finish setup" : "Set up payouts"}</Button>}
        </div>
      </section>

      <section className="mt-8">
        <h3 className="mb-3 font-bold">Recent payments</h3>
        {data?.rows?.length ? (
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
            {data.rows.slice(0, 50).map((r, i) => (
              <li key={r.id || i} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <div className="min-w-[200px] flex-1">
                  <p className="font-semibold">{r.listing_title || r.title || "Booking"}</p>
                  <p className="text-xs text-muted-foreground">{r.customer_name || ""}{r.created_at ? ` · ${prettyDate(r.created_at)}` : ""}{r.method || r.provider ? ` · ${r.method || r.provider}` : ""}</p>
                </div>
                <span className="text-xs text-muted-foreground">fee {gbp(r.fable_fee || 0)}</span>
                <span className="w-20 text-right font-semibold">{gbp(r.net ?? r.gross ?? r.amount)}</span>
              </li>
            ))}
          </ul>
        ) : data === undefined ? <Skeleton className="h-40 rounded-2xl" /> : <Empty title="No payments yet" body="Card and cash payments you record show up here with the fee breakdown." />}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- plan
export function PartnerPlan() {
  usePageMeta("Plan & billing");
  const { user, refresh } = useAuth();
  const plan = user?.plan;
  const subActive = user?.subscription_status === "active";
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll move to the free tier at the end of the period.")) return;
    setBusy(true);
    try {
      await billingApi.cancel();
      await refresh();
      toast.success("Subscription cancelled");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 className="mb-5 text-xl font-bold">Plan & billing</h2>
      {plan ? (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 text-sm" data-testid="plan-current">
          <span>Current plan: <b className="text-primary">{plan.tier_name || plan.tier}</b>{plan.monthly > 0 ? ` · £${Number(plan.monthly).toFixed(2)}/mo` : plan.billing_model === "payg" ? " · 2.7% per digital booking" : ""}</span>
          {plan.monthly > 0 && <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${subActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{subActive ? "Subscription active" : "Payment pending — choose the plan again to pay"}</span>}
          {subActive && <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={cancel} disabled={busy}>Cancel subscription</Button>}
        </div>
      ) : null}
      <PlanPicker />
    </div>
  );
}

// ---------------------------------------------------------------------------------------- Stripe returns
export function BillingReturn() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("Confirming your payment with Stripe…");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setMsg(params.get("cancel") ? "Checkout cancelled — nothing was charged." : "Nothing to confirm."); return; }
    let tries = 0;
    const tick = async () => {
      try {
        const s = await billingApi.status(sid);
        if (s.paid && (s.provider_status === "active" || s.subscription_status === "active")) {
          await refresh();
          toast.success(s.listing ? `“${s.listing.title}” is live` : "Your plan is active");
          navigate("/partner", { replace: true });
          return;
        }
        if (s.checkout_status === "expired") { setMsg("That checkout expired. Choose your plan again to pay."); return; }
      } catch { /* keep polling */ }
      if (++tries < 40) setTimeout(tick, 1500);
      else setMsg("Stripe hasn't confirmed yet. We'll unlock everything automatically the moment it lands — you can close this page.");
    };
    tick();
  }, [params, refresh, navigate]);
  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center">
      <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground" data-testid="billing-return-msg">{msg}</p>
      <Link to="/partner/plan" className="mt-6 inline-block text-sm font-semibold text-primary hover:underline">Back to plans</Link>
    </div>
  );
}

export function ConnectReturn() {
  const navigate = useNavigate();
  useEffect(() => {
    providerApi.connectStatus().catch(() => null).finally(() => navigate("/partner/earnings", { replace: true }));
  }, [navigate]);
  return <div className="mx-auto max-w-md px-6 py-24 text-center text-sm text-muted-foreground">Back from Stripe — checking your payout account…</div>;
}
